import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "../../../components/select";
import Input from "../../../components/input";
import TextArea from "../../../components/textArea";
import Button from "../../../components/button";
import { useDispatch, useSelector } from "react-redux";
import { createVolumeFromSnapshot } from "../../../hooks/local/userReducer";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import PaymentModal from "../../../components/paymentModal";
import useZoneUnitPrices from "../../../hooks/useZoneUnitPrices";

const CreateVolumeFromSnapshot = () => {
  document.title = "Create Volume From Server Snapshot - CloudVantage";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const projectId = useSelector((state) => state.user.selectedProject)?.id;
  const {getUnitPrice, loading: unitPriceLoading, } = useZoneUnitPrices(projectId);
  const currency = useSelector((state) => state.user.currency);
  const walletBalance = useSelector((state) =>
    state.user.virtualAccount
      ? currency === "USD"
        ? state.user.virtualAccount.dollar_balance
        : state.user.virtualAccount.balance
      : 0
  );
  const virtualAccount = useSelector((state) => state.user.virtualAccount);
  const selectedSnapshot = JSON.parse(sessionStorage.getItem("selectedServerSnapshot"));

  const parsedSnapshotSize = selectedSnapshot?.snapshotSize
    ? typeof selectedSnapshot?.snapshotSize === "number"
      ? selectedSnapshot?.snapshotSize
      : parseFloat(selectedSnapshot?.snapshotSize?.replace(/[^0-9.]/g, ""))
    : 0;

  const volumeSizeOptions = [
    { value: "", label: "Select an option" },
    { value: "5", label: "5 GB" },
    { value: "10", label: "10 GB" },
    { value: "15", label: "15 GB" },
    { value: "20", label: "20 GB" },
    { value: "25", label: "25 GB" },
    { value: "30", label: "30 GB" },
    { value: "35", label: "35 GB" },
    { value: "40", label: "40 GB" },
    { value: "45", label: "45 GB" },
    { value: "50", label: "50 GB" },
    { value: "100", label: "100 GB" },
    { value: "150", label: "150 GB" },
    { value: "200", label: "200 GB" },
    { value: "250", label: "250 GB" },
    { value: "500", label: "500 GB" },
    { value: "750", label: "750 GB" },
    { value: "1000", label: "1 TB" },
  ].filter((option) => !option.value || parseInt(option.value) >= parsedSnapshotSize);

  const volumePaymentOptions = [
    { value: "", label: "Select an option" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

const calculateEstimatedCost = (
  volumeSize,
  currency,
  paymentMethod
) => {
  if (!volumeSize || !paymentMethod) {
    return {
      formatted: "0.00",
      numeric: 0,
    };
  }

  const volumeSizePrice = getUnitPrice(
    "volume",
    currency
  );

  if (volumeSizePrice === null) {
    return {
      formatted: "Price unavailable",
      numeric: 0,
    };
  }

  const volumeSizeValue = parseFloat(volumeSize);

  let totalCost =
    volumeSizeValue * volumeSizePrice;

  if (paymentMethod === "yearly") {
    const yearlyPrice = totalCost * 12;
    const discount = yearlyPrice * 0.01;

    totalCost = yearlyPrice - discount;
  }

  return {
    formatted: totalCost.toLocaleString("en-US", {
      style: "currency",
      currency: currency === "NGN" ? "NGN" : "USD",
    }),
    numeric: totalCost,
  };
};

  const [calculatedEstimate, setCalculatedEstimate] = useState("0.00");
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const createVolume = useFormik({
    initialValues: {
      volumeName: "",
      size: parsedSnapshotSize ? String(parsedSnapshotSize) : "",
      payment: "",
      description: "",
    },
    validationSchema: Yup.object({
      volumeName: Yup.string().required("Volume name cannot be empty"),
      size: Yup.string()
        .required("Size is required")
        .test(
          "min-size",
          `Size must be equal to or greater than the snapshot size (${parsedSnapshotSize} GB)`,
          (value) => !value || parseInt(value) >= parsedSnapshotSize
        ),
      payment: Yup.string().required("Payment method is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      const { volumeName, size, payment, description } = values;

      let volumeData = {
        name: volumeName,
        snapshot_id: selectedSnapshot?.snapshotId || "",
        size: parseInt(size),
        projectId,
        payment_method: payment,
        description,
      };

      const { payload } = await dispatch(createVolumeFromSnapshot({ volumeData, navigate }));
      if (payload.statuscode === "00") {
        navigate("/client-area/volumes");
        resetForm();
      }
    },
  });

  useEffect(() => {
    const estimate = calculateEstimatedCost(
      createVolume.values.size,
      currency,
      createVolume.values.payment
    );
    setCalculatedEstimate(estimate.formatted);
  }, [createVolume.values.size, createVolume.values.payment, currency]);

  const handleCreateVolumeClick = (e) => {
    e.preventDefault();

    const estimate = calculateEstimatedCost(
      createVolume.values.size,
      currency,
      createVolume.values.payment
    )?.numeric;

    if (estimate > walletBalance) {
      setShowPaymentModal(true);
    } else {
      createVolume.handleSubmit();
    }
  };

  return (
    <div className="min-h-screen px-4 md:px-10 pt-6 pb-10 dark:text-white">
      <p className="crimson-font text-lg font-medium text-primaryBlue mb-6">
        Create Volume from Snapshot {selectedSnapshot?.snapshotName}
      </p>

      <div className="grid gap-4">
        <form className="gap-y-6 grid">
          <div className="w-full lg:w-[50%] grid grid-cols-1 items-start gap-4">
            <Input
              type={"text"}
              label="Volume Name"
              name={"volumeName"}
              onBlur={createVolume.handleBlur}
              onChange={createVolume.handleChange}
              placeholder={"Give your volume a name"}
              value={createVolume.values.volumeName}
              onError={
                createVolume.errors.volumeName && createVolume.touched.volumeName
                  ? createVolume.errors.volumeName
                  : null
              }
            />
            <Select
              label="Size:"
              name="size"
              options={volumeSizeOptions}
              value={createVolume.values.size}
              valueKey={"value"}
              labelKey={"label"}
              selectValue={createVolume.values.size}
              selectBlur={createVolume.handleBlur}
              onChange={(event) => createVolume.setFieldValue("size", event.target.value)}
              onError={
                createVolume.touched.size && createVolume.errors.size
                  ? createVolume.errors.size
                  : null
              }
            />
            <Select
              label="Payment:"
              name="payment"
              options={volumePaymentOptions}
              valueKey={"value"}
              labelKey={"label"}
              selectValue={createVolume.values.payment}
              selectBlur={createVolume.handleBlur}
              onChange={(event) => createVolume.setFieldValue("payment", event.target.value)}
              onError={
                createVolume.touched.payment && createVolume.errors.payment
                  ? createVolume.errors.payment
                  : null
              }
            />
            <TextArea
              label="Description"
              name="description"
              value={createVolume.values.description}
              onBlur={createVolume.handleBlur}
              onChange={createVolume.handleChange}
              onError={
                createVolume.errors.description && createVolume.touched.description
                  ? createVolume.errors.description
                  : null
              }
            />
            <div className="flex justify-between items-center gap-2">
              <div>
                <div className="text-xs">Estimated cost:</div>
                <div className="font-semibold crimson-font text-primaryBlue text-lg">
                  {calculatedEstimate}
                </div>
              </div>
             <Button
              buttonRole="submit"
              disabled={unitPriceLoading}
              buttonText={
                unitPriceLoading
                  ? "Loading price..."
                  : calculateEstimatedCost(
                      createVolume.values.size,
                      currency,
                      createVolume.values.payment
                    ).numeric > walletBalance
                    ? "Pay Now"
                    : "Create Volume"
              }
              handleClick={(e) => handleCreateVolumeClick(e)}
            />
            </div>
          </div>
        </form>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        currency={currency}
        totalPrice={calculateEstimatedCost(
          createVolume.values.size,
          currency,
          createVolume.values.payment
        ).numeric}
        walletBalance={walletBalance}
        virtualAccount={virtualAccount}
        onPaymentSuccess={() => createVolume.handleSubmit()}
      />
    </div>
  );
};

export default CreateVolumeFromSnapshot;

